import { DateTime } from 'luxon';

import {
  categoryPercent,
  countCategories,
  DIVERSION_THRESHOLD_PERCENT,
  EstimationVariancePoint,
  VarianceCategory,
} from '@/features/TimeStatsModel';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { getTrackerUrl } from '@/utils/getTrackerUrl';

export const CATEGORY_LABELS: Record<VarianceCategory, string> = {
  onTimeOrFaster: 'В соответствии с оценкой или быстрее',
  within30: 'Превышение до 30%',
  over30: 'Превышение более 30%',
};

export const CATEGORY_COLORS: Record<VarianceCategory, string> = {
  onTimeOrFaster: 'var(--success-color)',
  within30: 'var(--info-color)',
  over30: 'var(--danger-color)',
};

export type CategoryChartItem = {
  name: string;
  category: VarianceCategory;
  value: number;
  percent: number;
};

export function formatMinutes(minutes: number): string {
  return formatDurationRussian(minutes) || '0м';
}

export function formatSignedPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  const sign = rounded > 0 ? '+' : '';

  return `${sign}${rounded}%`;
}

export function formatYAxisTick(value: number): string {
  if (value === DIVERSION_THRESHOLD_PERCENT) {
    return '+30%';
  }

  if (value === 0) {
    return '0%';
  }

  return `${value}%`;
}

export function getTaskKeyFromScatterClick(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const withPayload = data as { payload?: { taskKey?: string }; taskKey?: string };

  return withPayload.payload?.taskKey || withPayload.taskKey;
}

export function openTaskInTracker(data: unknown) {
  const taskKey = getTaskKeyFromScatterClick(data);

  if (!taskKey) {
    return;
  }

  window.open(getTrackerUrl(taskKey), '_blank', 'noopener,noreferrer');
}

export function xAxisTicks(domain: [number, number]): number[] {
  const ticks: number[] = [];
  let cursor = DateTime.fromMillis(domain[0]).startOf('day');
  const end = DateTime.fromMillis(domain[1]).startOf('day');

  while (cursor <= end) {
    ticks.push(cursor.toMillis());
    cursor = cursor.plus({ days: 1 });
  }

  if (ticks.length <= 16) {
    return ticks;
  }

  const step = Math.ceil(ticks.length / 12);
  const reduced = ticks.filter((_, index) => index % step === 0 || index === ticks.length - 1);

  return reduced;
}

export function buildCategoryChartData(points: EstimationVariancePoint[]): {
  categoryCounts: Record<VarianceCategory, number>;
  pieData: CategoryChartItem[];
  totalPlottedTasks: number;
} {
  const categoryCounts = countCategories(points);
  const totalPlottedTasks = points.length;
  const pieData = (Object.keys(categoryCounts) as VarianceCategory[])
    .map((category) => ({
      name: CATEGORY_LABELS[category],
      category,
      value: categoryCounts[category],
      percent: categoryPercent(categoryCounts[category], totalPlottedTasks),
    }))
    .filter((item) => item.value > 0);

  return { categoryCounts, pieData, totalPlottedTasks };
}
